import {
  Check,
  GripVertical,
  ImageOff,
  Loader2,
  Pencil,
  Plus,
  ShoppingBag,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Switch } from '@/components/ui/switch'
import { SearchPriceInput } from '@/components/form/search-price-input'
import { useCreateOffer, useOffersList, useUpdateOffer, type OfferItem } from '@/lib/api/offers'
import { useProduct } from '@/lib/api/products'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatMoney } from '@/lib/money'
import { cn } from '@/lib/utils'
import React from 'react'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { RuntimeStepConfig } from '../types'

const OFFER_TYPE = 'checkout_offer'

interface CheckoutOfferStepConfigProps {
  storeId: string
  value: RuntimeStepConfig['checkoutOffers']
  onChange: (config: RuntimeStepConfig['checkoutOffers']) => void
}

type FormState = {
  title: string
  productId: string
  variantId: string
  priceId: string
  quantity: number
}

const INITIAL_FORM: FormState = {
  title: '',
  productId: '',
  variantId: '',
  priceId: '',
  quantity: 1,
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(amount / 100)
  } catch {
    return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`
  }
}

function offerPriceDisplay(offer: OfferItem): string | null {
  const summary = (offer.summaries as Array<{ currency?: string; totalAdjustedAmount?: number }> | null | undefined)?.[0]
  if (!summary?.currency || typeof summary.totalAdjustedAmount !== 'number') return null
  return formatCurrency(summary.totalAdjustedAmount, summary.currency)
}

function offerImageUrl(offer: OfferItem): string | null {
  const firstLine = offer.offerLineItems?.[0] as any
  return firstLine?.price?.variant?.imageUrl ?? null
}

function offerProductName(offer: OfferItem): string | null {
  const firstLine = offer.offerLineItems?.[0] as any
  return firstLine?.price?.variant?.product?.name ?? null
}

function offerTitle(offer: OfferItem): string {
  const t = (offer.titleTrans as Record<string, string> | null | undefined)?.en
  return t || offer.id
}

// ─── Sortable Card ──────────────────────────────────────────────────

function OfferCard({
  offer,
  isEnabled,
  isDefault,
  showToggle,
  showDefaultRadio,
  showDragHandle,
  onToggle,
  onMakeDefault,
  onEdit,
  description,
  comparePrice,
  onChangeOverrides,
}: {
  offer: OfferItem
  isEnabled: boolean
  isDefault: boolean
  showToggle: boolean
  showDefaultRadio: boolean
  showDragHandle: boolean
  onToggle: (enabled: boolean) => void
  onMakeDefault: () => void
  onEdit: () => void
  description: string
  comparePrice: string
  onChangeOverrides: (next: { description?: string; comparePrice?: number }) => void
}) {
  const { t } = useTranslation('storefront')
  const sortable = useSortable({ id: offer.id, disabled: !showDragHandle })
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable
  const imageUrl = offerImageUrl(offer)
  const productName = offerProductName(offer) || t('checkoutOffer.unknownProduct')
  const priceDisplay = offerPriceDisplay(offer)
  const title = offerTitle(offer)

  // Local string state for the compare-price input so the user can type
  // "10.50" naturally — keep the raw text while focused, only commit a
  // normalized cents value to onChangeOverrides on blur. The previous
  // approach round-tripped through Number() on every keystroke, which
  // forced toFixed(2) display ("1" -> "0.01") and made digits jump.
  const [comparePriceDraft, setComparePriceDraft] = React.useState(comparePrice)
  React.useEffect(() => {
    setComparePriceDraft(comparePrice)
  }, [comparePrice])

  const commitComparePrice = () => {
    const raw = comparePriceDraft.trim()
    if (raw === '') {
      if (comparePrice !== '') onChangeOverrides({ comparePrice: undefined })
      return
    }
    const cents = Math.round(Number(raw) * 100)
    if (!Number.isFinite(cents) || cents < 0) {
      setComparePriceDraft(comparePrice) // revert to last committed
      return
    }
    onChangeOverrides({ comparePrice: cents })
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
      }}
      className={cn(
        'rounded-lg border transition-all',
        isEnabled ? 'border-border bg-card' : 'border-transparent bg-muted/30 opacity-60',
        isDefault && 'ring-1 ring-primary/40',
      )}
    >
    <div
      role={showDefaultRadio && isEnabled ? 'button' : undefined}
      onClick={
        showDefaultRadio && isEnabled
          ? (e) => {
              const target = e.target as HTMLElement
              if (target.closest('button, input, [role="switch"], [role="radio"], [data-drag-handle], [data-override-input]')) return
              onMakeDefault()
            }
          : undefined
      }
      className={cn(
        'flex items-center gap-2.5 px-3 py-2',
        isEnabled && 'cursor-pointer hover:bg-muted/30',
      )}
    >
      {showDragHandle && (
        <button
          type="button"
          data-drag-handle
          {...attributes}
          {...listeners}
          className="shrink-0 cursor-grab rounded p-0.5 text-muted-foreground/40 hover:bg-muted hover:text-foreground active:cursor-grabbing"
          aria-label={t('checkoutOffer.reorder')}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}

      {showDefaultRadio && (
        <RadioGroupItem
          value={offer.id}
          id={`co-default-${offer.id}`}
          className="h-3.5 w-3.5 shrink-0"
          disabled={!isEnabled}
        />
      )}

      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md border',
          isEnabled ? 'border-border bg-white dark:bg-slate-800' : 'border-transparent bg-muted/50',
        )}
      >
        {imageUrl ? (
          <img src={imageUrl} alt={productName} className="h-full w-full object-cover" />
        ) : (
          <ImageOff className="h-3.5 w-3.5 text-muted-foreground/40" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className={cn('truncate text-xs font-medium', !isEnabled && 'text-muted-foreground')}>
            {title}
          </span>
          {priceDisplay && (
            <Badge variant="secondary" className="shrink-0 text-[10px] font-semibold tabular-nums">
              {priceDisplay}
            </Badge>
          )}
          {isDefault && (
            <Badge variant="outline" className="h-4 border-primary/40 bg-primary/10 px-1.5 text-[9px] font-normal text-primary">
              {t('checkoutOffer.defaultBadge')}
            </Badge>
          )}
        </div>
        <p className="truncate text-[10px] text-muted-foreground">{productName}</p>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md p-1 text-muted-foreground/50 transition-colors hover:bg-muted hover:text-foreground"
        >
          <Pencil className="h-3 w-3" />
        </button>
        {showToggle && (
          <Switch checked={isEnabled} onCheckedChange={onToggle} className="shrink-0 scale-90" />
        )}
      </div>
    </div>
    {isEnabled && (
      <div className="border-t border-border/60 px-3 py-2 grid grid-cols-2 gap-2">
        <div className="space-y-0.5">
          <Label className="text-[10px] text-muted-foreground">
            {t('checkoutOffer.descriptionOverride')}
          </Label>
          <Input
            data-override-input
            value={description}
            onChange={(e) => onChangeOverrides({ description: e.target.value })}
            placeholder={t('checkoutOffer.descriptionPlaceholder')}
            className="h-7 text-xs"
          />
        </div>
        <div className="space-y-0.5">
          <Label className="text-[10px] text-muted-foreground">
            {t('checkoutOffer.comparePriceOverride')}
          </Label>
          <Input
            data-override-input
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={comparePriceDraft}
            onChange={(e) => setComparePriceDraft(e.target.value)}
            onBlur={commitComparePrice}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitComparePrice()
              }
            }}
            placeholder="0.00"
            className="h-7 text-xs"
          />
        </div>
      </div>
    )}
    </div>
  )
}

// ─── Shared inline form ─────────────────────────────────────────────

function OfferFormFields({
  form,
  setForm,
  storeId,
  autoFocusTitle,
}: {
  form: FormState
  setForm: (updater: (prev: FormState) => FormState) => void
  storeId: string
  autoFocusTitle?: boolean
}) {
  const { t } = useTranslation('storefront')
  const { data: selectedProduct } = useProduct(form.productId, !!form.productId)

  const availablePrices = React.useMemo(() => {
    if (!selectedProduct?.variants) return []
    return selectedProduct.variants.flatMap((v: any) =>
      (v.prices ?? []).map((p: any) => ({ ...p, variantId: v.id, variantName: v.name })),
    )
  }, [selectedProduct])

  const showPriceSelector = availablePrices.length > 1 && !!form.productId

  return (
    <>
      <div className="space-y-1">
        <Label className="text-xs">{t('checkoutOffer.offerName')}</Label>
        <Input
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder={t('checkoutOffer.offerNamePlaceholder')}
          className="h-8 text-sm"
          autoFocus={autoFocusTitle}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">{t('checkoutOffer.productAndPrice')}</Label>
        <SearchPriceInput
          storeId={storeId}
          priceId={form.priceId || undefined}
          onChange={(selection) => {
            setForm((f) => ({
              ...f,
              productId: selection.productId,
              variantId: selection.variantId,
              priceId: selection.priceId,
            }))
          }}
        />
      </div>

      {showPriceSelector && (
        <div className="space-y-1">
          <Label className="text-xs">{t('checkoutOffer.price')}</Label>
          <Select
            value={form.priceId}
            onValueChange={(newPriceId) => {
              const selected = availablePrices.find((p: any) => p.id === newPriceId)
              if (selected) {
                setForm((f) => ({
                  ...f,
                  priceId: newPriceId,
                  variantId: selected.variantId,
                }))
              }
            }}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availablePrices.map((p: any) => {
                const co = p.currencyOptions
                const cur = co ? Object.keys(co)[0] : null
                const amt = cur && co?.[cur] ? co[cur].amount : 0
                const recurring = p.recurring && p.interval ? ` / ${p.interval}` : ''
                const label = `${p.variantName} · ${cur ? formatMoney(amt, cur.toUpperCase()) : 'N/A'}${recurring}`
                return (
                  <SelectItem key={p.id} value={p.id} className="text-sm">
                    {label}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1">
        <Label className="text-xs">{t('checkoutOffer.quantity')}</Label>
        <Input
          type="number"
          min={1}
          max={999}
          step={1}
          value={form.quantity}
          onChange={(e) => {
            const raw = Number(e.target.value)
            const clamped = Number.isFinite(raw) ? Math.min(999, Math.max(1, Math.round(raw))) : 1
            setForm((f) => ({ ...f, quantity: clamped }))
          }}
          className="h-8 text-sm"
        />
      </div>
    </>
  )
}

// ─── Main component ─────────────────────────────────────────────────

export function CheckoutOfferStepConfig({ storeId, value, onChange }: CheckoutOfferStepConfigProps) {
  const { t } = useTranslation('storefront')
  const { data, isLoading } = useOffersList({ storeId, type: OFFER_TYPE }, { enabled: !!storeId })

  const createMutation = useCreateOffer()
  const updateMutation = useUpdateOffer()

  const [showInlineForm, setShowInlineForm] = React.useState(false)
  const [inlineForm, setInlineForm] = React.useState<FormState>(INITIAL_FORM)
  const [editingOfferId, setEditingOfferId] = React.useState<string | null>(null)
  const [editForm, setEditForm] = React.useState<FormState | null>(null)

  const mode = value?.mode ?? 'inherit'
  const enabledOfferIds = value?.enabledOfferIds ?? []
  const defaultOfferId = value?.defaultOfferId
  const offerSettings = (value?.offerSettings ?? {}) as Record<string, { description?: string; comparePrice?: number }>

  // Per-offer presentation overrides (description, comparePrice). Empty
  // values are dropped so the schema never persists noise like
  // `{ description: '' }`.
  const handleChangeOverrides = React.useCallback(
    (offerId: string, partial: { description?: string; comparePrice?: number }) => {
      const current = offerSettings[offerId] ?? {}
      const merged = { ...current, ...partial }
      if (merged.description === '' || merged.description == null) delete merged.description
      if (merged.comparePrice == null) delete merged.comparePrice

      const nextSettings = { ...offerSettings }
      if (Object.keys(merged).length === 0) {
        delete nextSettings[offerId]
      } else {
        nextSettings[offerId] = merged
      }

      const nextValue: NonNullable<typeof value> = {
        ...(value ?? { mode: 'inherit' }),
      } as NonNullable<typeof value>
      if (Object.keys(nextSettings).length > 0) {
        (nextValue as any).offerSettings = nextSettings
      } else {
        delete (nextValue as any).offerSettings
      }
      onChange(nextValue)
    },
    [offerSettings, value, onChange],
  )

  const allOffers = (data?.offers ?? []) as OfferItem[]
  const offersById = React.useMemo(() => {
    const map = new Map<string, OfferItem>()
    for (const o of allOffers) map.set(o.id, o)
    return map
  }, [allOffers])

  // In custom mode, render in enabledOfferIds order (drag-to-reorder source of truth).
  // In inherit mode, render in the API-returned order.
  const orderedOffers = React.useMemo<OfferItem[]>(() => {
    if (mode === 'custom') {
      const seen = new Set<string>()
      const out: OfferItem[] = []
      for (const id of enabledOfferIds) {
        const o = offersById.get(id)
        if (o && !seen.has(id)) {
          out.push(o)
          seen.add(id)
        }
      }
      for (const o of allOffers) {
        if (!seen.has(o.id)) out.push(o)
      }
      return out
    }
    return allOffers
  }, [mode, enabledOfferIds, allOffers, offersById])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const handleModeChange = (newMode: string) => {
    if (newMode === 'inherit') {
      onChange({ mode: 'inherit', defaultOfferId })
    } else {
      onChange({
        mode: 'custom',
        enabledOfferIds: allOffers.map((o) => o.id),
        defaultOfferId: defaultOfferId ?? allOffers[0]?.id,
      })
    }
  }

  const handleToggleOffer = (offerId: string, enabled: boolean) => {
    const set = new Set(enabledOfferIds)
    if (enabled) set.add(offerId)
    else set.delete(offerId)
    const next = Array.from(set)
    const nextDefault = defaultOfferId && next.includes(defaultOfferId) ? defaultOfferId : next[0]
    onChange({ mode: 'custom', enabledOfferIds: next, defaultOfferId: nextDefault })
  }

  const handleSetDefault = (offerId: string) => {
    if (mode === 'custom') {
      onChange({ mode: 'custom', enabledOfferIds, defaultOfferId: offerId })
    } else {
      onChange({ mode: 'inherit', defaultOfferId: offerId })
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    // In inherit mode there's no enabledOfferIds to reorder yet — auto-promote
    // to custom with every current offer enabled in the rendered order, then
    // apply the drag. Merchants get the order they want without having to
    // toggle the mode radio first.
    const baseOrder =
      mode === 'custom' ? enabledOfferIds : orderedOffers.map((o) => o.id)
    const oldIndex = baseOrder.indexOf(String(active.id))
    const newIndex = baseOrder.indexOf(String(over.id))
    if (oldIndex === -1 || newIndex === -1) return
    onChange({
      mode: 'custom',
      enabledOfferIds: arrayMove(baseOrder, oldIndex, newIndex),
      defaultOfferId: defaultOfferId ?? baseOrder[0],
    })
  }

  const handleInlineCreate = async () => {
    if (!inlineForm.title.trim() || !inlineForm.priceId) return
    try {
      await createMutation.mutateAsync({
        storeId,
        type: OFFER_TYPE,
        titleTrans: { en: inlineForm.title.trim() },
        lineItems: [{ priceId: inlineForm.priceId, quantity: inlineForm.quantity }],
      })
      setInlineForm(INITIAL_FORM)
      setShowInlineForm(false)
      if (mode === 'custom') onChange({ mode: 'inherit', defaultOfferId })
    } catch {
      // toast via the mutation hook
    }
  }

  const handleStartEdit = (offer: OfferItem) => {
    const firstLine = offer.offerLineItems?.[0] as any
    setEditingOfferId(offer.id)
    setEditForm({
      title: offerTitle(offer),
      productId: firstLine?.productId ?? '',
      variantId: firstLine?.variantId ?? '',
      priceId: firstLine?.priceId ?? '',
      quantity: firstLine?.quantity ?? 1,
    })
    setShowInlineForm(false)
  }

  const handleCancelEdit = () => {
    setEditingOfferId(null)
    setEditForm(null)
  }

  const handleSaveEdit = async () => {
    if (!editForm || !editingOfferId) return
    if (!editForm.title.trim() || !editForm.priceId) return
    try {
      await updateMutation.mutateAsync({
        offerId: editingOfferId,
        storeId,
        type: OFFER_TYPE,
        titleTrans: { en: editForm.title.trim() },
        lineItems: [{ priceId: editForm.priceId, quantity: editForm.quantity }],
      })
      handleCancelEdit()
    } catch {
      // toast via the mutation hook
    }
  }

  const isInlineValid = inlineForm.title.trim() && inlineForm.priceId
  const isEditValid = editForm && editForm.title.trim() && editForm.priceId

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-2.5">
      {allOffers.length > 0 && (
        <RadioGroup value={mode} onValueChange={handleModeChange} className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="inherit" id="co-inherit" className="h-3.5 w-3.5" />
            <Label htmlFor="co-inherit" className="cursor-pointer text-xs">
              {t('checkoutOffer.allOffers')}
            </Label>
            <Badge variant="secondary" className="text-[10px]">
              {allOffers.length}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="custom" id="co-custom" className="h-3.5 w-3.5" />
            <Label htmlFor="co-custom" className="cursor-pointer text-xs">
              {t('checkoutOffer.pickForThisStep')}
            </Label>
            {mode === 'custom' && (
              <Badge variant="secondary" className="text-[10px]">
                {enabledOfferIds.length}/{allOffers.length}
              </Badge>
            )}
          </div>
        </RadioGroup>
      )}

      {allOffers.length > 0 ? (
        <RadioGroup
          value={defaultOfferId ?? orderedOffers[0]?.id ?? ''}
          onValueChange={handleSetDefault}
          className={cn('space-y-1 overflow-y-auto', allOffers.length > 4 && 'max-h-[280px]')}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedOffers.map((o) => o.id)}
              strategy={verticalListSortingStrategy}
            >
              {orderedOffers.map((offer) => {
                const isEnabled = mode === 'inherit' || enabledOfferIds.includes(offer.id)
                const isDefault = isEnabled && offer.id === (defaultOfferId ?? orderedOffers[0]?.id)

                if (editingOfferId === offer.id && editForm) {
                  return (
                    <div key={offer.id} className="space-y-2.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-semibold uppercase tracking-wide">
                          {t('checkoutOffer.editOffer')}
                        </h4>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      <OfferFormFields
                        form={editForm}
                        setForm={setEditForm as any}
                        storeId={storeId}
                        autoFocusTitle
                      />

                      <div className="flex items-center gap-2 pt-0.5">
                        <Button
                          type="button"
                          size="sm"
                          className="h-7 gap-1.5 text-xs"
                          disabled={!isEditValid || updateMutation.isPending}
                          onClick={handleSaveEdit}
                        >
                          {updateMutation.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Check className="h-3 w-3" />
                          )}
                          {updateMutation.isPending ? t('checkoutOffer.saving') : t('checkoutOffer.save')}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={handleCancelEdit}
                        >
                          {t('checkoutOffer.cancel')}
                        </Button>
                      </div>
                    </div>
                  )
                }

                const settings = offerSettings[offer.id] ?? {};
                const comparePriceCents = settings.comparePrice;
                return (
                  <OfferCard
                    key={offer.id}
                    offer={offer}
                    isEnabled={isEnabled}
                    isDefault={isDefault}
                    showToggle={mode === 'custom'}
                    showDefaultRadio={isEnabled}
                    showDragHandle={isEnabled}
                    onToggle={(checked) => handleToggleOffer(offer.id, checked)}
                    onMakeDefault={() => handleSetDefault(offer.id)}
                    onEdit={() => handleStartEdit(offer)}
                    description={settings.description ?? ''}
                    comparePrice={
                      typeof comparePriceCents === 'number'
                        ? (comparePriceCents / 100).toFixed(2)
                        : ''
                    }
                    onChangeOverrides={(next) => handleChangeOverrides(offer.id, next)}
                  />
                )
              })}
            </SortableContext>
          </DndContext>
        </RadioGroup>
      ) : (
        !showInlineForm && (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-6 text-center">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
              <ShoppingBag className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs font-medium">{t('checkoutOffer.noOffersYet')}</p>
              <p className="text-[10px] text-muted-foreground">{t('checkoutOffer.createTieredOffers')}</p>
            </div>
          </div>
        )
      )}

      {showInlineForm ? (
        <div className="space-y-2.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wide">{t('checkoutOffer.newOffer')}</h4>
            <button
              type="button"
              onClick={() => {
                setShowInlineForm(false)
                setInlineForm(INITIAL_FORM)
              }}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <OfferFormFields form={inlineForm} setForm={setInlineForm} storeId={storeId} autoFocusTitle />

          <div className="flex items-center gap-2 pt-0.5">
            <Button
              type="button"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              disabled={!isInlineValid || createMutation.isPending}
              onClick={handleInlineCreate}
            >
              {createMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Check className="h-3 w-3" />
              )}
              {createMutation.isPending ? t('checkoutOffer.creating') : t('checkoutOffer.createAndEnable')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setShowInlineForm(false)
                setInlineForm(INITIAL_FORM)
              }}
            >
              {t('checkoutOffer.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-1.5 h-8 text-xs"
          onClick={() => setShowInlineForm(true)}
        >
          <Plus className="h-3 w-3" />
          {t('checkoutOffer.newOffer')}
        </Button>
      )}
    </div>
  )
}
